// src/pages/Agents/Agents.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import backendURL from '../../config';
import { Link } from 'react-router-dom';
import './Agents.css';

export default function Agents() {
    const [agents, setAgents] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchAgentStatus = useCallback(async () => {
        try {
            const token = localStorage.getItem("access_token");
            const response = await axios.get(`${backendURL}/api/agents/status`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAgents(response.data.agents);
        } catch (err) {
            setError('Failed to load agent statuses.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = 'Manage Agents | Klonit';
        fetchAgentStatus();
    }, [fetchAgentStatus]);

    const handleToggle = async (agentName, newStatus) => {
        setAgents(prev => ({
            ...prev,
            [agentName]: { ...prev[agentName], is_active: newStatus }
        }));

        try {
            const token = localStorage.getItem("access_token");
            await axios.post(`${backendURL}/api/agents/status`,
                { agent_name: agentName, is_active: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
        } catch (err) {
            setError(`Failed to update ${agentName} status.`);
            setAgents(prev => ({
                ...prev,
                [agentName]: { ...prev[agentName], is_active: !newStatus }
            }));
        }
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div className="alert alert-danger">{error}</div>;

    const renderAgentCard = (agentName, config) => {
        const { is_active, is_configured } = config;
        
        // --- MODIFIED: Use a switch to handle different agents ---
        let link, description;
        switch (agentName) {
            case 'ROUTER':
                link = '/agents/router';
                description = 'Configures the main routing agent that directs queries to the correct specialist.';
                break;
            case 'GOOGLE_CALENDAR':
                link = '/agents/google-calendar';
                description = 'Allows the AI to manage your Google Calendar events.';
                break;
            case 'MYSQL':
                link = '/agents/mysql';
                description = 'Allows the AI to query your MySQL database.';
                break;
            default:
                link = '#';
                description = 'Unknown agent.';
        }
        
        return (
            <div className="col-md-6 mb-4" key={agentName}>
                <div className="card agent-card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                            <div>
                                <h5 className="card-title">{agentName.replace('_', ' ')} Agent</h5>
                                <h6 className={`card-subtitle mb-2 ${is_configured ? 'text-success' : 'text-warning'}`}>
                                    {is_configured ? 'Configured' : 'Not Configured'}
                                </h6>
                            </div>
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    checked={is_active}
                                    onChange={(e) => handleToggle(agentName, e.target.checked)}
                                    // Router and configured agents can be toggled. Unconfigured specialists cannot.
                                    disabled={!is_configured && agentName !== 'ROUTER'}
                                    id={`switch-${agentName}`}
                                />
                                <label className="form-check-label" htmlFor={`switch-${agentName}`}>
                                    {is_active ? 'Active' : 'Inactive'}
                                </label>
                            </div>
                        </div>
                        <p className="card-text">{description}</p>
                        <Link to={link} className="btn btn-outline-primary">
                            {is_configured ? 'Manage Configuration' : 'Configure'}
                        </Link>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <main className="content">
            <div className="container-fluid p-0">
                <h1 className="h3 mb-3"><strong>Manage Your AI Agents</strong></h1>
                <div className="row">
                    {/* --- MODIFIED: Added a sort function to show Router agent first --- */}
                    {agents && Object.entries(agents)
                        .filter(([name]) => name !== 'ROUTER')
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([name, config]) => renderAgentCard(name, config))}
                </div>
            </div>
        </main>
    );
}